import { Dropdown, Tooltip } from 'antd/lib';
import { useEffect, useState } from 'react';
import { AlertCircle, Check, CheckCircle, Info, Loader, MoreHorizontal } from 'react-feather';

import { Button } from '@/components';
import { TextInput } from '@/components/text-input';
import { SelectInput, SelectOption } from '@/components/select-input/SelectInput';
import { useAppContext } from '@/context';
import { useOtpVerify, useUserUpdate } from '@/hooks';
import { synchronizeLanguage } from '@/utils/languages';

import i18n, { SUPPORTED } from '@/i18n/config';
import { useTranslation } from 'react-i18next';
import OtpModal from '../modals/OtpModal';
import styles from '../Settings.module.css';

interface GeneralSectionProps {
  onUpdate: (user: Api.UserObject) => void;
  onFormChange: (data: null | FormDataType) => void;
  user: Api.UserObject;
}

export interface FormDataType {
  first_name: string;
  last_name: string;
  phone: string;
  language: Api.Language | string;
}

// Language configuration with flags and native names
const LANGUAGE_CONFIG = {
  en: { flag: '🇺🇸', name: 'English (US)' },
  sv: { flag: '🇸🇪', name: 'Svenska' },
  fr: { flag: '🇫🇷', name: 'Français' },
  es: { flag: '🇪🇸', name: 'Español' },
  de: { flag: '🇩🇪', name: 'Deutsch' },
  pl: { flag: '🇵🇱', name: 'Polski' }
} as const;

// Generate language options dynamically from supported languages
const LANGUAGES: SelectOption[] = SUPPORTED.map((lang) => ({
  value: lang,
  label: `${LANGUAGE_CONFIG[lang].flag} ${LANGUAGE_CONFIG[lang].name}`
})).sort((a, b) => {
  // English first, then alphabetical order
  if (a.value === 'en') return -1;
  if (b.value === 'en') return 1;
  return a.label.localeCompare(b.label);
});

export function GeneralSection({ onUpdate, user, onFormChange }: GeneralSectionProps) {
  const [language, setLanguage] = useState<string | null>(null);
  const [verify, setVerify] = useState<boolean>(false);
  const [otpPopup, setOtpPopup] = useState<boolean>(false);
  const [otpValues, setOtpValues] = useState<string[]>([]);
  const [formData, setFormData] = useState<FormDataType>(() => ({
    first_name: user.first_name || '',
    last_name: user.last_name || '',
    phone: user.phone || '',
    language: user.language || ''
  }));
  const [errors, setErrors] = useState<Partial<FormDataType>>({});

  const { t } = useTranslation();
  const { setAlert } = useAppContext();
  const { mutate: verifyOtp, isLoading: isVerifying } = useOtpVerify({
    otp: otpValues.join(''),
    onSuccess: (res) => {
      if (res && (res as Api.UserObject).user_id) {
        onUpdate(res as Api.UserObject);
        setOtpPopup(false);
        setAlert('SAVED');
      }
    }
  });
  const { mutate, isLoading } = useUserUpdate({
    ...formData,
    language: formData.language as Api.Language,
    send_phone_verification_sms: verify,
    onSuccess: (res) => {
      if (res && (res as Api.UserObject).user_id) {
        if (verify) {
          setOtpPopup(true);
        } else {
          if (i18n.language !== (res as Api.UserObject).language) {
            i18n.changeLanguage((res as Api.UserObject).language);
          }

          onUpdate(res as Api.UserObject);
          onFormChange(null);
        }
        if (!otpPopup) {
          setAlert('SAVED');
        }
      }
    }
  });

  useEffect(() => {
    const lang = LANGUAGES.find((item) => item.value === user.language);
    setLanguage(lang?.value.toString() || LANGUAGES[0].value.toString());

    setFormData({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      phone: user.phone || '',
      language: user.language || ''
    });
  }, [user]);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    onFormChange({
      ...formData,
      [name]: value
    });
    setErrors({
      ...errors,
      [name]: ''
    });
  }

  function handleChangeLang(value: string) {
    setLanguage(value);
    setFormData({
      ...formData,
      language: value
    });
    onFormChange({
      ...formData,
      language: value
    });
    setErrors({
      ...errors,
      language: ''
    });

    // Immediately change the app's language without waiting for save
    synchronizeLanguage(value, 'settings-language-change');
  }

  function phoneVerify() {
    setVerify(true);
    setTimeout(() => mutate(), 0);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setVerify(false);

    const newErrors: Partial<FormDataType> = {};
    const requiredFields: (keyof FormDataType)[] = ['first_name', 'language'];
    requiredFields.forEach((key) => {
      if (!formData[key]) {
        newErrors[key] = t('account.reqired');
      }
    });

    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }

    mutate();
  }

  function dropdownRender(conditioanal: boolean, func: () => void) {
    return (
      <div className={styles.dropdown}>
        <div className={styles.dropdownItem} onClick={func}>
          {conditioanal ? <Loader className={styles.animate} size={16} /> : <Check size={16} />}
          <span>{t('account.verification.button')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.section}>
      <div className={styles.sectionInfo}>
        <div className={styles.sectionInfoTitle}>{t('account.generalSection.title')}</div>
        <div className={styles.sectionInfoDescription}>
          {t('account.generalSection.description')}
        </div>
      </div>
      <div className={styles.sectionBody}>
        <div className={styles.sectionTable}>
          <div className={styles.row}>
            <div className={styles.grow}>
              <div className={styles.sectionBodyItem}>
                <TextInput
                  value={formData.first_name}
                  placeholder={t('placeholder.firstName')}
                  required
                  name="first_name"
                  label={t('account.firstName')}
                  error={errors.first_name}
                  onChange={handleChange}
                />
                <TextInput
                  value={formData.last_name}
                  placeholder={t('placeholder.lastName')}
                  required
                  name="last_name"
                  label={t('account.lastName')}
                  error={errors.last_name}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className={styles.fitContent} />
          </div>
          <div className={styles.row}>
            <div className={styles.grow}>
              <SelectInput
                value={language}
                label={t('account.language')}
                labelTooltip={
                  <Tooltip title={t('account.generalSection.language')}>
                    <Info size={16} style={{ color: '#474E5450' }} />
                  </Tooltip>
                }
                options={LANGUAGES}
                onChange={handleChangeLang}
              />
            </div>
            <div className={styles.fitContent} />
          </div>
          <div className={styles.row}>
            <div className={styles.grow}>
              <TextInput
                value={formData.phone}
                placeholder={t('placeholder.phone')}
                name="phone"
                label={t('account.phoneNumber')}
                error={errors.phone}
                onChange={handleChange}
                suffix={
                  <div>
                    {formData.phone && (
                      <>
                        <div className={styles.smBlock}>
                          {user.phone_is_verified ? (
                            <CheckCircle
                              size={16}
                              style={{ color: '#7AD098', marginRight: '5px' }}
                            />
                          ) : (
                            <Dropdown
                              dropdownRender={() =>
                                dropdownRender(verify && isLoading, phoneVerify)
                              }
                              placement="bottomRight"
                              trigger={['click']}
                            >
                              <MoreHorizontal size={16} style={{ cursor: 'pointer' }} />
                            </Dropdown>
                          )}
                        </div>
                        <div className={styles.smNone}>
                          {user.phone_is_verified ? (
                            <CheckCircle
                              size={16}
                              style={{ color: '#7AD098', marginRight: '5px' }}
                            />
                          ) : (
                            <Tooltip title={t('account.emailSection.verify')}>
                              <AlertCircle size={16} style={{ color: 'red', marginRight: '5px' }} />
                            </Tooltip>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                }
              />
            </div>
            <div className={styles.fitContent}>
              {formData.phone && !user.phone_is_verified && (
                <Button
                  type="button"
                  disabled={!formData.phone}
                  isLoading={verify && isLoading}
                  onClick={phoneVerify}
                  className={styles.emailButton}
                >
                  {t('account.verification.button')}
                </Button>
              )}
            </div>
          </div>
        </div>
        <Button
          type="button"
          theme="cta"
          onClick={handleSubmit}
          isLoading={!verify && isLoading}
          style={{ width: '140px' }}
        >
          {t('account.save')}
        </Button>
      </div>
      {otpPopup && (
        <OtpModal
          onClose={() => setOtpPopup(false)}
          onChange={(data: string[]) => setOtpValues(data)}
          isLoading={isVerifying}
          onNext={verifyOtp}
        />
      )}
    </div>
  );
}
